import { CheckCircle2, Eye } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DOCUMENT_CLASSIFICATIONS } from "@/constants/documents";

import { stripBundlePrefix } from "@utils/bundle-name";

import { FileWithMetadata } from "./types";

type DocumentTableProps = {
  files: FileWithMetadata[];
  selectedFilename?: string;
  getClassification: (file: FileWithMetadata) => string;
  selectedClassification?: string;
  getPageRange: (file: FileWithMetadata) => string;
  onFileSelect: (file: FileWithMetadata) => void;
  onClassificationChange: (file: FileWithMetadata, value: string) => void;
};

export const DocumentTable = ({
  files,
  selectedFilename,
  getClassification,
  selectedClassification,
  getPageRange,
  onFileSelect,
  onClassificationChange,
}: DocumentTableProps) => (
  <section className="overflow-y-auto">
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-2/5">Document Title</TableHead>
          <TableHead className="w-2/5">Classification</TableHead>
          <TableHead className="w-1/5">Page Range</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {files.map((file, index) => {
          const isSelected = selectedFilename === file.filename;
          return (
            <TableRow
              key={index}
              className={
                isSelected
                  ? "cursor-pointer bg-primary/10 hover:bg-primary/20"
                  : "hover:bg-muted cursor-pointer"
              }
              onClick={() => onFileSelect(file)}
            >
              <TableCell>
                <div className="flex min-w-0 items-center gap-3 font-medium">
                  {isSelected ? (
                    <Eye className="h-4 w-4 shrink-0 text-primary" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  )}
                  <span
                    className="min-w-0 truncate"
                    title={stripBundlePrefix(file.filename)}
                  >
                    {stripBundlePrefix(file.filename)}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <Select
                  value={
                    isSelected ? selectedClassification : getClassification(file)
                  }
                  onValueChange={(value) => onClassificationChange(file, value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select classification" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_CLASSIFICATIONS.map(({ title, category }) => (
                      <SelectItem key={category} value={category}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>{getPageRange(file)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </section>
);
